import { getScheduleDeviation } from "../vehicle/scheduleDeviation";

export const STOP_ETA_MAX_MINUTES = 60;
export const STOP_DEVIATION_MAX_MINUTES = 60;

function pluralUnit(count, singular, plural) {
  return count === 1 ? singular : plural;
}

function durationLabel(minutes, seconds) {
  const parts = [];
  if (minutes >= 1) {
    parts.push(`${minutes} ${pluralUnit(minutes, "minute", "minutes")}`);
  }
  if (seconds >= 1) {
    parts.push(`${seconds} ${pluralUnit(seconds, "second", "seconds")}`);
  }
  return parts.join(" ");
}

export function formatStopAnnouncement({
  vehicleName,
  destination,
  phase,
  minutes,
  seconds = 0,
}) {
  const lead = `${vehicleName} towards ${destination} will reach the stop`;
  const count = Math.max(0, Number(minutes) || 0);
  const remainder = Math.max(0, Number(seconds) || 0);
  const duration = durationLabel(count, phase === "stop_eta" ? remainder : 0);

  if (phase === "stop_delay") {
    return `${lead} with a delay of ${duration}.`;
  }
  if (phase === "stop_early") {
    return `${lead} ${duration} early.`;
  }
  return `${lead} in ${duration}.`;
}

export function splitAnnouncementTime(etaSeconds) {
  if (!Number.isFinite(etaSeconds) || etaSeconds <= 0) return null;
  const total = Math.round(etaSeconds);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes > STOP_ETA_MAX_MINUTES) return null;
  if (minutes === STOP_ETA_MAX_MINUTES && seconds > 0) return null;
  if (minutes === 0 && seconds === 0) return null;
  return { minutes, seconds };
}

export function buildStopAnnouncement({
  vehicleId,
  vehicleName,
  destination,
  etaSeconds,
  stopId,
  tripStatus,
  now = Date.now(),
}) {
  const spokenTime = splitAnnouncementTime(etaSeconds);
  if (!vehicleName || !destination || !spokenTime) return null;

  const deviation = getScheduleDeviation(
    tripStatus,
    stopId,
    etaSeconds,
    now,
    tripStatus?.delayThresholdSeconds,
  );

  let schedule = null;
  if (deviation?.status === "early" && deviation.minutes >= 1) {
    const minutes = Math.min(STOP_DEVIATION_MAX_MINUTES, deviation.minutes);
    schedule = {
      phase: "stop_early",
      minutes,
      seconds: 0,
      text: formatStopAnnouncement({
        vehicleName,
        destination,
        phase: "stop_early",
        minutes,
      }),
    };
  } else if (
    deviation?.status === "late" &&
    deviation.minutes >= 1 &&
    deviation.withinBuffer !== true
  ) {
    const minutes = Math.min(STOP_DEVIATION_MAX_MINUTES, deviation.minutes);
    schedule = {
      phase: "stop_delay",
      minutes,
      seconds: 0,
      text: formatStopAnnouncement({
        vehicleName,
        destination,
        phase: "stop_delay",
        minutes,
      }),
    };
  }

  return {
    vehicleId,
    vehicleName,
    destination,
    phase: "stop_eta",
    minutes: spokenTime.minutes,
    seconds: spokenTime.seconds,
    text: formatStopAnnouncement({
      vehicleName,
      destination,
      phase: "stop_eta",
      minutes: spokenTime.minutes,
      seconds: spokenTime.seconds,
    }),
    schedule,
    dedupeKey: `${vehicleId}:stop_eta:${spokenTime.minutes}:${schedule?.phase || "none"}:${schedule?.minutes || 0}`,
  };
}

export function tripStatusSignature(event) {
  if (!event) return "";
  const arrivals = Array.isArray(event.scheduledStops)
    ? event.scheduledStops.map((stop) => `${stop.stopId}:${stop.scheduledArrival}`).join(",")
    : "";
  return [
    event.scheduleId ?? "",
    event.occurrenceKey ?? "",
    event.occurrenceEnd ?? "",
    event.delayThresholdSeconds ?? "",
    arrivals,
  ].join("|");
}

export function announcementLanguagesFromSettings(settings) {
  const fallback = String(settings?.defaultLanguage || "en").trim().toLowerCase() || "en";
  const configured = Array.isArray(settings?.multilingualLanguages)
    ? settings.multilingualLanguages
    : [];
  const languages = configured.length > 0 ? configured : [fallback];
  return Array.from(
    new Set(
      languages
        .map((language) => String(language || "").trim().toLowerCase())
        .filter(Boolean),
    ),
  );
}
